import { migrateLegacyTimeTags, type FilterState, type SortKey, type DatePreset } from './eventFilters';

const STORAGE_KEY = 'mh:saved-views:v1';

/**
 * A serialised filter state. Sets become arrays so the value round-trips
 * cleanly through `JSON.stringify` / `localStorage`.
 */
export interface SavedView {
  id: string;
  name: string;
  createdAt: string;
  filters: SerializedFilters;
}

interface SerializedFilters {
  search: string;
  topics?: string[];
  times?: string[];
  topicsExcluded?: string[];
  timesExcluded?: string[];
  categories: string[];
  neighborhoods: string[];
  venues: string[];
  sources: string[];
  tags: string[];
  categoriesExcluded?: string[];
  neighborhoodsExcluded?: string[];
  venuesExcluded?: string[];
  sourcesExcluded?: string[];
  tagsExcluded?: string[];
  preset: DatePreset;
  customFrom?: string;
  customTo?: string;
  hasImage: boolean;
  hasTicket: boolean;
  sort: SortKey;
  recurringMode?: 'show-all' | 'hide-instances' | 'only-repeats' | 'hide-weekly';
  venueVisibility?: 'active-only' | 'with-dismissed' | 'all';
}

export function serializeFilters(f: FilterState): SerializedFilters {
  return {
    search: f.search,
    topics: [...f.topics],
    times: [...f.times],
    topicsExcluded: [...f.topicsExcluded],
    timesExcluded: [...f.timesExcluded],
    categories: [...f.categories],
    neighborhoods: [...f.neighborhoods],
    venues: [...f.venues],
    sources: [...f.sources],
    tags: [...f.tags],
    categoriesExcluded: [...f.categoriesExcluded],
    neighborhoodsExcluded: [...f.neighborhoodsExcluded],
    venuesExcluded: [...f.venuesExcluded],
    sourcesExcluded: [...f.sourcesExcluded],
    tagsExcluded: [...f.tagsExcluded],
    preset: f.preset,
    customFrom: f.customFrom,
    customTo: f.customTo,
    hasImage: f.hasImage,
    hasTicket: f.hasTicket,
    sort: f.sort,
    recurringMode: f.recurringMode,
    venueVisibility: f.venueVisibility,
  };
}

export function deserializeFilters(s: SerializedFilters): FilterState {
  return migrateLegacyTimeTags({
    search: s.search ?? '',
    topics: new Set(s.topics ?? []),
    times: new Set(s.times ?? []),
    topicsExcluded: new Set(s.topicsExcluded ?? []),
    timesExcluded: new Set(s.timesExcluded ?? []),
    categories: new Set(s.categories ?? []),
    neighborhoods: new Set(s.neighborhoods ?? []),
    venues: new Set(s.venues ?? []),
    sources: new Set(s.sources ?? []),
    tags: new Set(s.tags ?? []),
    categoriesExcluded: new Set(s.categoriesExcluded ?? []),
    neighborhoodsExcluded: new Set(s.neighborhoodsExcluded ?? []),
    venuesExcluded: new Set(s.venuesExcluded ?? []),
    sourcesExcluded: new Set(s.sourcesExcluded ?? []),
    tagsExcluded: new Set(s.tagsExcluded ?? []),
    preset: s.preset ?? '60d',
    customFrom: s.customFrom,
    customTo: s.customTo,
    hasImage: !!s.hasImage,
    hasTicket: !!s.hasTicket,
    sort: s.sort ?? 'date-asc',
    // Saved views are filter snapshots, not view-setting snapshots. The
    // "Show hidden" toggle, like origin/distance, intentionally resets to
    // its default when applying a saved view.
    showExcludedTags: false,
    recurringMode: s.recurringMode ?? 'show-all',
    venueVisibility: s.venueVisibility ?? 'with-dismissed',
  });
}

/** Read the saved-view list. Never throws — corrupt storage returns []. */
export function loadSavedViews(): SavedView[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (v): v is SavedView =>
        v && typeof v.id === 'string' && typeof v.name === 'string' && v.filters
    );
  } catch {
    return [];
  }
}

function persist(views: SavedView[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(views));
  } catch {
    // Storage full / disabled — saved views are a convenience, not critical.
  }
}

/** Save a new view (or overwrite by case-insensitive name match). Returns the updated list. */
export function saveView(name: string, filters: FilterState): SavedView[] {
  const trimmed = name.trim();
  if (!trimmed) return loadSavedViews();
  const views = loadSavedViews();
  const lower = trimmed.toLowerCase();
  const existing = views.findIndex((v) => v.name.trim().toLowerCase() === lower);
  const next: SavedView = {
    id: existing >= 0 ? views[existing].id : crypto.randomUUID(),
    name: trimmed,
    createdAt: existing >= 0 ? views[existing].createdAt : new Date().toISOString(),
    filters: serializeFilters(filters),
  };
  if (existing >= 0) views[existing] = next;
  else views.push(next);
  views.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  persist(views);
  return views;
}

export function deleteView(id: string): SavedView[] {
  const views = loadSavedViews().filter((v) => v.id !== id);
  persist(views);
  return views;
}
